/**
 * LICENSE REQUIRED PAGE
 *
 * Full-page blocking component displayed when the backend returns 402 (License Required).
 * Replaces all app content to prevent access to any functionality.
 *
 * Shows:
 * - Lock icon in a circular warning container
 * - "License Required" heading
 * - Message from the API response
 * - "Get License" button linking to Atlassian Marketplace
 *
 * Props:
 * - message: string — error message from the 402 API response
 * - compact: boolean — if true, renders a smaller version for standalone panels (IssuePanel, etc.)
 */

import React from 'react'
import Button from '@atlaskit/button'
import LockIcon from '@atlaskit/icon/glyph/lock'

const LicenseRequiredPage = ({ message, compact = false }) => {
  // Compact version for standalone Jira panels (IssuePanel, LinkScenariosPage)
  if (compact) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 16px',
        textAlign: 'center'
      }}>
        <LockIcon
          label="License required"
          size="large"
          primaryColor="var(--ds-icon-warning, #FF8B00)"
        />
        <h3 style={{
          margin: '12px 0 8px 0',
          fontSize: '16px',
          fontWeight: 500,
          color: 'var(--ds-text, #172B4D)'
        }}>
          License Required
        </h3>
        <p style={{
          margin: '0 0 16px 0',
          fontSize: '12px',
          color: 'var(--ds-text-subtlest, #6B778C)',
          lineHeight: '16px'
        }}>
          {message || 'A valid license is required to use this application.'}
        </p>
        <Button
          appearance="primary"
          spacing="compact"
          href="https://marketplace.atlassian.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          Get License
        </Button>
      </div>
    )
  }

  // Full-page version for main app layout
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      padding: '48px 24px',
      textAlign: 'center',
      backgroundColor: 'var(--ds-surface-sunken, #F4F5F7)'
    }}>
      {/* Lock icon container */}
      <div style={{
        width: 80,
        height: 80,
        borderRadius: '50%',
        backgroundColor: 'var(--ds-background-warning, #FFF7D6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 24
      }}>
        <LockIcon
          label="License required"
          size="xlarge"
          primaryColor="var(--ds-icon-warning, #FF8B00)"
        />
      </div>

      {/* Heading */}
      <h2 style={{
        margin: '0 0 12px 0',
        fontSize: '24px',
        fontWeight: 500,
        color: 'var(--ds-text, #172B4D)',
        lineHeight: '28px'
      }}>
        License Required
      </h2>

      {/* Message from API */}
      <p style={{
        margin: '0 0 32px 0',
        fontSize: '14px',
        color: 'var(--ds-text-subtlest, #6B778C)',
        maxWidth: 480,
        lineHeight: '20px'
      }}>
        {message || 'A valid license is required to use this application. Please install or renew your license from the Atlassian Marketplace.'}
      </p>

      {/* CTA button */}
      <Button
        appearance="primary"
        href="https://marketplace.atlassian.com"
        target="_blank"
        rel="noopener noreferrer"
      >
        Get License
      </Button>
    </div>
  )
}

export default LicenseRequiredPage
